import { Form, Input, InputNumber } from "antd";
import type { InputRef } from "antd";
import Select, { BaseOptionType } from "antd/lib/select";
import { useEffect, useRef } from "react";
import { useAppSelector } from "../../../../../app/hooks";
import {
  selectIsKlearlyAdmin,
  selectIsKlearlySuperAdmin,
} from "../../../../access-control/accountSlice";
import { isValidWebsiteUrl } from "../../../../../utils/isValidWebsiteUrl";
import { selectCurrentBusiness } from "../../../businessesSlice";
import { isNoEdit } from "../../../utils/isNoEdit";

interface ICompanyProfileEditorProps {
  // Bumped by the parent to request focus/scroll to the Website field, e.g. when
  // a submit-to-Adyen attempt was rejected for a missing website.
  focusWebsiteSignal?: number;
}

export default function CompanyProfileEditor({
  focusWebsiteSignal,
}: ICompanyProfileEditorProps) {
  const isKlearlySuperAdmin = useAppSelector(selectIsKlearlySuperAdmin);
  const isKlearlyAdmin = useAppSelector(selectIsKlearlyAdmin);
  const currentBusiness = useAppSelector(selectCurrentBusiness);
  // Support contact is frozen once the merchant is submitted to Adyen (the
  // backend returns supportPhone/supportEmail in noEdit and 422s on change).
  const supportPhoneDisabled = isNoEdit(
    currentBusiness?.noEdit,
    "supportPhone"
  );
  const supportEmailDisabled = isNoEdit(
    currentBusiness?.noEdit,
    "supportEmail"
  );
  // Only users who can submit a merchant to Adyen onboarding may edit the website.
  const canEditWebsite = isKlearlyAdmin;
  const websiteInputRef = useRef<InputRef>(null);
  const bankFieldLayout = {
    labelCol: { span: 12 },
    wrapperCol: { span: 12 },
  };

  useEffect(() => {
    if (!focusWebsiteSignal || !canEditWebsite) return;
    // The parent switches to this tab in the same update, so wait one frame for
    // the pane to become visible before focusing/scrolling (focus() and
    // scrollIntoView no-op on a display:none element).
    const raf = requestAnimationFrame(() => {
      const input = websiteInputRef.current;
      if (!input) return;
      input.focus();
      input.input?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(raf);
  }, [focusWebsiteSignal, canEditWebsite]);
  const getBusinessTypeOptions = () => {
    return [
      { label: "Sole Trader", value: "soleTrader" },
      { label: "Public Limited Company", value: "publicLimitedCompany" },
      { label: "Private Limited Company", value: "privateLimitedCompany" },
      { label: "Partnership", value: "partnership" },
      { label: "Other", value: "other" },
    ] as BaseOptionType[];
  };
  return (
    <>
      <Form.Item
        wrapperCol={{ ...bankFieldLayout.wrapperCol }}
        labelCol={{ ...bankFieldLayout.labelCol }}
        label="Business Name"
        name="businessName"
        labelAlign="left"
      >
        <Input></Input>
      </Form.Item>
      <Form.Item
        wrapperCol={{ ...bankFieldLayout.wrapperCol }}
        labelCol={{ ...bankFieldLayout.labelCol }}
        label="Business Trade Name"
        name="businessTradeName"
        labelAlign="left"
      >
        <Input></Input>
      </Form.Item>

      {canEditWebsite && (
        <Form.Item
          wrapperCol={{ ...bankFieldLayout.wrapperCol }}
          labelCol={{ ...bankFieldLayout.labelCol }}
          label="Website"
          name="websiteURL"
          labelAlign="left"
          validateFirst
          validateTrigger="onBlur"
          rules={[
            {
              validator: (_rule, value) => {
                if (
                  value === undefined ||
                  value === null ||
                  value.trim() === ""
                ) {
                  return Promise.resolve();
                }
                return isValidWebsiteUrl(value)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        `Invalid website "${value.trim()}" — must be a valid http(s) URL`
                      )
                    );
              },
            },
          ]}
        >
          <Input
            ref={websiteInputRef}
            placeholder="https://merchant.example"
            allowClear
          />
        </Form.Item>
      )}

      <Form.Item
        wrapperCol={{ ...bankFieldLayout.wrapperCol }}
        labelCol={{ ...bankFieldLayout.labelCol }}
        label="Support Phone"
        name="supportPhone"
        labelAlign="left"
      >
        <Input
          placeholder="+3112345678"
          disabled={supportPhoneDisabled}
          allowClear
        />
      </Form.Item>
      <Form.Item
        wrapperCol={{ ...bankFieldLayout.wrapperCol }}
        labelCol={{ ...bankFieldLayout.labelCol }}
        label="Support Email"
        name="supportEmail"
        labelAlign="left"
      >
        <Input
          placeholder="help@merchant.example"
          disabled={supportEmailDisabled}
          allowClear
        />
      </Form.Item>

      <Form.Item
        wrapperCol={{ ...bankFieldLayout.wrapperCol }}
        labelCol={{ ...bankFieldLayout.labelCol }}
        label="Business Type"
        name="businessType"
        labelAlign="left"
      >
        <Select style={{ minWidth: 190 }} options={getBusinessTypeOptions()} />
      </Form.Item>
      <Form.Item
        wrapperCol={{ ...bankFieldLayout.wrapperCol }}
        labelCol={{ ...bankFieldLayout.labelCol }}
        label="VAT"
        name="vat"
        labelAlign="left"
      >
        <InputNumber addonAfter="%" min={0} />
      </Form.Item>

      <div
        style={{
          padding: "20px 10px",
          border: "solid rgba(227, 227, 227, 0.49) 1px",
        }}
      >
        <Form.Item
          wrapperCol={{ ...bankFieldLayout.wrapperCol }}
          labelCol={{ ...bankFieldLayout.labelCol }}
          rules={[
            {
              required: true,
              message: "Missing Business Registration #",
            },
          ]}
          label="Business Registration #"
          name="businessRegistrationNumber"
          labelAlign="left"
          help="Only super admins can edit this field. Please contact support if you need to change this."
        >
          <Input disabled={!isKlearlySuperAdmin}></Input>
        </Form.Item>
        <Form.Item
          wrapperCol={{ ...bankFieldLayout.wrapperCol }}
          labelCol={{ ...bankFieldLayout.labelCol }}
          label="VAT Number"
          name="vatNumber"
          labelAlign="left"
        >
          <Input disabled={!isKlearlyAdmin}></Input>
        </Form.Item>
        <Form.Item
          wrapperCol={{ ...bankFieldLayout.wrapperCol }}
          labelCol={{ ...bankFieldLayout.labelCol }}
          label="Tax ID"
          name="tinNumber"
          labelAlign="left"
        >
          <Input disabled={!isKlearlyAdmin}></Input>
        </Form.Item>
      </div>
    </>
  );
}
